import { Loader2, MoreHorizontal, Search, ShieldCheck } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import {
  useAdminUsers,
  useApproveUser,
  useBlockUser,
  useDeleteUser,
  useMakeAdmin,
  useRevokeAdmin,
  useUnblockUser,
} from "@/entities/admin/queries";
import type { AdminUser, UserStatus } from "@/entities/admin/types";
import { ApiError } from "@/shared/api/http";
import { useAuthStore } from "@/shared/lib/auth-store";
import { cn } from "@/shared/lib/cn";
import { formatDate } from "@/shared/lib/format";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/ui/alert-dialog";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function AdminUsersTable(): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const [status, setStatus] = React.useState<UserStatus>("all");
  const [q, setQ] = React.useState("");
  const debouncedQ = useDebounced(q, 300);

  const { data, isLoading, isError } = useAdminUsers({
    status,
    q: debouncedQ || undefined,
    limit: 100,
    offset: 0,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по email…"
            className="h-9 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-muted-foreground"
          />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as UserStatus)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="pending">Ожидают</SelectItem>
            <SelectItem value="approved">Одобрены</SelectItem>
            <SelectItem value="blocked">Заблокированы</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonTable />
      ) : isError || !data ? (
        <div className="glass-card p-6 text-sm text-pink">
          Не удалось загрузить пользователей.
        </div>
      ) : data.items.length === 0 ? (
        <div className="glass-card p-12 text-center text-sm text-muted-foreground">
          Ничего не найдено
        </div>
      ) : (
        <div className="glass-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="border-b border-white/10">
                <Th>Email</Th>
                <Th>Статус</Th>
                <Th>Админ</Th>
                <Th>В whitelist</Th>
                <Th>Создан</Th>
                <Th className="w-12 text-right" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((u) => (
                <UserRow key={u.id} user={u} isMe={me?.id === u.id} />
              ))}
            </tbody>
          </table>
          <div className="border-t border-white/10 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground">
            Показано {data.items.length} из {data.total}
          </div>
        </div>
      )}
    </div>
  );
}

function UserRow({ user, isMe }: { user: AdminUser; isMe: boolean }): JSX.Element {
  const status: "pending" | "approved" | "blocked" = user.is_blocked
    ? "blocked"
    : user.is_approved
      ? "approved"
      : "pending";

  return (
    <tr
      className={cn(
        "border-b border-white/10 last:border-0",
        status === "pending" && "bg-amber/[0.05]",
      )}
    >
      <td className="px-3 py-2.5 align-middle">
        <span className="text-foreground/95">{user.email}</span>
        {isMe && <span className="ml-2 text-[10px] uppercase text-muted-foreground">— это вы</span>}
      </td>
      <td className="px-3 py-2.5 align-middle">
        <StatusPill status={status} />
      </td>
      <td className="px-3 py-2.5 align-middle">
        {user.is_admin ? (
          <Badge variant="amber">
            <ShieldCheck className="h-3 w-3" /> admin
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 align-middle text-xs">
        {user.in_whitelist ? (
          <span className="text-green">✓</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 align-middle text-xs text-muted-foreground">
        {formatDate(user.created_at)}
      </td>
      <td className="px-3 py-2.5 text-right">
        <RowActions user={user} isMe={isMe} status={status} />
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: "pending" | "approved" | "blocked" }): JSX.Element {
  const map = {
    pending: { color: "text-amber", bg: "bg-amber/10", border: "border-amber/35", label: "Ожидает" },
    approved: { color: "text-green", bg: "bg-green/10", border: "border-green/35", label: "Одобрен" },
    blocked: { color: "text-pink", bg: "bg-pink/10", border: "border-pink/35", label: "Заблокирован" },
  } as const;
  const s = map[status];
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold", s.bg, s.border, s.color)}>
      {s.label}
    </span>
  );
}

function RowActions({
  user,
  isMe,
  status,
}: {
  user: AdminUser;
  isMe: boolean;
  status: "pending" | "approved" | "blocked";
}): JSX.Element {
  const approve = useApproveUser();
  const block = useBlockUser();
  const unblock = useUnblockUser();
  const makeAdminMut = useMakeAdmin();
  const revokeAdminMut = useRevokeAdmin();
  const del = useDeleteUser();

  const busy =
    approve.isPending || block.isPending || unblock.isPending ||
    makeAdminMut.isPending || revokeAdminMut.isPending || del.isPending;

  const run = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(label);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Не удалось выполнить");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-white/15 bg-white/[0.04]"
          disabled={busy}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MoreHorizontal className="h-3.5 w-3.5" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        {status === "pending" && (
          <DropdownMenuItem onClick={() => run("Одобрено", () => approve.mutateAsync(user.id))}>
            Одобрить
          </DropdownMenuItem>
        )}
        {status === "approved" && (
          <DropdownMenuItem
            disabled={isMe}
            onClick={() => run("Заблокирован", () => block.mutateAsync(user.id))}
          >
            Заблокировать
          </DropdownMenuItem>
        )}
        {status === "blocked" && (
          <DropdownMenuItem onClick={() => run("Разблокирован", () => unblock.mutateAsync(user.id))}>
            Разблокировать
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {user.is_admin ? (
          <DropdownMenuItem
            disabled={isMe}
            onClick={() => run("Права админа сняты", () => revokeAdminMut.mutateAsync(user.id))}
          >
            Снять права админа
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => run("Назначен админом", () => makeAdminMut.mutateAsync(user.id))}>
            Сделать админом
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <DropdownMenuItem
              disabled={isMe}
              onSelect={(e) => e.preventDefault()}
              className="text-pink focus:bg-pink/10"
            >
              Удалить
            </DropdownMenuItem>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить пользователя?</AlertDialogTitle>
              <AlertDialogDescription>
                Все его запуски и ключи будут удалены. Восстановить нельзя.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => run("Пользователь удалён", () => del.mutateAsync(user.id))}
              >
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }): JSX.Element {
  return (
    <th
      className={cn(
        "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
    >
      {children}
    </th>
  );
}

function SkeletonTable(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
